import { observer } from 'mobx-react-lite'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useMemo, useState } from 'react'
import Card from '@mui/material/Card'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import SkeletonLoader from 'reusable/mui/skeleton/SkeletonLoader'
import { themeConfig }from '@gap/shared/theme'
import Chip, { ChipProps } from '@mui/material/Chip'
import Fade from '@mui/material/Fade'

export type IntegrationStatus = 'connected' | 'not connected' | 'connection lost'

/** Label for the hover action button. A lost connection re-connects rather than disconnects. */
function statusActionLabel(params: { status: Exclude<IntegrationStatus, 'not connected'>, loading: boolean }): string {
   if (params.status === 'connected') return params.loading ? 'Disconnecting' : 'Disconnect'
   return params.loading ? 'Connecting' : 'Re-connect'
}

export interface AdvisorIntegrationCardProps {
   icon: React.ReactNode
   title: string
   subTitle: string
   status: IntegrationStatus
   onClick: () => void
   loading?: boolean
   /**
    * When provided and status is 'connected', replaces the chip + hover-disconnect
    * affordance with a button action (e.g. "Sync now").
    */
   connectedAction?: {
      label: string
      onClick: () => void
      loading?: boolean
   }
   /**
    * Pending state for the Connect button while a redirect is in flight. `loading` shows a spinner
    * and blocks repeat clicks; `disabled` greys it out while a sibling card is mid-connect.
    */
   connectAction?: {
      loading?: boolean
      disabled?: boolean
   }
   /**
    * Pending state for the hover-revealed action button (Disconnect, or Re-connect after a lost
    * connection). `loading` pins the button visible, since it would otherwise vanish the moment
    * the pointer left, and shows a spinner in place of the label.
    */
   statusAction?: {
      loading?: boolean
      disabled?: boolean
   }
}

export default observer(function AdvisorIntegrationCard(props: AdvisorIntegrationCardProps) {
   const [hovered, setHovered] = useState(false)
   const onMouseEnter = () => setHovered(true)
   const onMouseLeave = () => setHovered(false)

   return <SkeletonLoader loading={props.loading || false} sx={{ height: 78.5, transform: 'none', borderRadius: '16px' }}>
      <Card
         sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            borderRadius: '16px',
            justifyContent: 'space-between',
            border: `1px solid ${themeConfig.primary[200]}`,
            ':hover': {
               backgroundColor: themeConfig.primary[100],
            }
         }}
         elevation={0}
         onMouseEnter={onMouseEnter}
         onMouseLeave={onMouseLeave}
      >
         <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {props.icon}
            <Box sx={{ ml: 2 }}>
               <Typography fontWeight={600}>{props.title}</Typography>
               <Typography variant="body2" color="text.secondary">
                  {props.subTitle}
               </Typography>
            </Box>
         </Box>

         {props.status !== 'not connected' ? (
            props.connectedAction
               ? <ConnectedActionButton {...props.connectedAction} />
               : <SmoothChipToButtonFade hovered={hovered} status={props.status} onClick={props.onClick} action={props.statusAction} />
         ) : <ButtonWithProgress
            variant="text"
            loading={props.connectAction?.loading}
            disabled={props.connectAction?.disabled}
            onClick={props.onClick}
         >
            {props.connectAction?.loading ? 'Connecting' : 'Connect'}
         </ButtonWithProgress>}
      </Card>
   </SkeletonLoader>
})

const ConnectedActionButton = observer(function ConnectedActionButton(props: { label: string, onClick: () => void, loading?: boolean }) {
   if (props.loading) {
      return <CircularProgress size={20} />
   }
   return <Button variant="text" onClick={props.onClick}>{props.label}</Button>
})

const SmoothChipToButtonFade = observer(function SmoothChipToButtonFade(props: { hovered: boolean, status: Exclude<IntegrationStatus, 'not connected'>, onClick: () => void, action?: { loading?: boolean, disabled?: boolean } }) {
   // Keep the button up while its request runs, so the feedback survives the pointer moving away.
   const showButton = props.hovered || !!props.action?.loading

   const chipProps: ChipProps = useMemo(() => {
      switch (props.status) {
         case 'connected':
            return {
               label: 'Connected',
               sx: {
                  backgroundColor: themeConfig.system.success[300]
               }
            }
         case 'connection lost':
            return {
               label: 'Connection lost',
               sx: {
                  backgroundColor: themeConfig.system.warning[300]
               }
            }

      }
   }, [props.status])

   return <Box sx={{ position: 'relative', minWidth: 130, mt: -3, display: 'inline-flex' }}>
      <Fade in={!showButton} timeout={300} unmountOnExit>
         <Box
            sx={{
               position: 'absolute',
               top: 0,
               left: 0,
               right: 0,
               display: 'flex',
               justifyContent: 'flex-end',
            }}
         >
            <Chip
               {...chipProps}
               size="small"
            />
         </Box>
      </Fade>
      <Fade in={showButton} timeout={300} unmountOnExit>
         <Box
            sx={{
               position: 'absolute',
               top: -6,
               left: 0,
               right: 0,
               display: 'flex',
               justifyContent: 'flex-end',
            }}
         >
            <ButtonWithProgress
               variant="text"
               onClick={props.onClick}
               size="small"
               loading={props.action?.loading}
               disabled={props.action?.disabled}
               sx={{ minWidth: 'fit-content', }}
            >
               {statusActionLabel({ status: props.status, loading: !!props.action?.loading })}
            </ButtonWithProgress>
         </Box>
      </Fade>
   </Box>
})